/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LES NATIONALITÉS — liste fermée, gentilé féminin, Guinéenne en tête.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Demande du fondateur, 24/09/2026 : « nationalité — liste déroulante et
 * recherche, guinéenne en premier ».
 *
 * ⚠️⚠️ CE QUE CE FICHIER CORRIGE, MESURÉ LE 24/09/2026.
 *   · `personnePhysiqueSchema.nationalite` est un `z.string().min(2).max(80)` :
 *     « Guinéenne », « guineen », « GUINEE » et « guinéen » sont quatre
 *     personnes différentes pour un rapprochement, et toutes s'impriment sur un
 *     acte.
 *   · Et pour les PARTIES, le guichet n'en demandait AUCUNE : `partie()`
 *     écrivait `nationalite: 'Guinéenne'` EN DUR dans `guichet.tsx`. Un
 *     donateur étranger était inscrit guinéen, en silence, sur un acte foncier.
 *
 * ⚠️ LE GENTILÉ EST AU FÉMININ, et ce n'est pas une préférence : c'est la forme
 * de l'état civil français dont dérive celui de la Guinée — « de nationalité
 * guinéenne ». Mélanger « Guinéen » et « Guinéenne » rouvrirait la divergence
 * que cette liste ferme.
 *
 * ⚠️ CE QUI EST STOCKÉ RESTE LE LIBELLÉ, PAS LE CODE, et c'est un arbitrage
 * assumé : `fon.demande.formulaire`, l'acte imprimé et `fon.titulaire` portent
 * déjà la chaîne. Passer aux codes ISO exigerait de reprendre les formulaires
 * déjà déposés et le rendu de l'acte — un chantier à part. Le code alpha-2 est
 * porté ici pour que cette bascule reste possible sans re-saisir la liste.
 *
 * ⚠️ CETTE LISTE EST UNE SAISIE HUMAINE, PAS UNE SOURCE CERTIFIÉE. Elle suit
 * ISO 3166-1 pour l'ensemble des États et les usages français pour les
 * gentilés. Avant tout usage opposable, elle doit être confrontée à une source
 * d'autorité — et `tests/nationalites.test.ts` ne garantit aujourd'hui que ses
 * invariants de forme (unicité, ordre, non-vacuité), jamais son exactitude.
 */

/** [code ISO 3166-1 alpha-2, gentilé féminin] */
export const NATIONALITES: ReadonlyArray<readonly [string, string]> = [
  // ── Le pays du registre, toujours en premier ──────────────────────────
  ['GN', 'Guinéenne'],

  // ── Les voisins immédiats et la CEDEAO ────────────────────────────────
  ['GW', 'Bissau-Guinéenne'], ['CI', 'Ivoirienne'], ['LR', 'Libérienne'],
  ['ML', 'Malienne'], ['SN', 'Sénégalaise'], ['SL', 'Sierra-Léonaise'],
  ['BJ', 'Béninoise'], ['BF', 'Burkinabè'], ['CV', 'Cap-Verdienne'],
  ['GM', 'Gambienne'], ['GH', 'Ghanéenne'], ['NE', 'Nigérienne'],
  ['NG', 'Nigériane'], ['TG', 'Togolaise'],

  // ── Le reste du monde, ordre alphabétique du gentilé ──────────────────
  ['AF', 'Afghane'], ['ZA', 'Sud-Africaine'], ['AL', 'Albanaise'],
  ['DZ', 'Algérienne'], ['DE', 'Allemande'], ['AD', 'Andorrane'],
  ['AO', 'Angolaise'], ['AG', 'Antiguaise-et-Barbudienne'], ['SA', 'Saoudienne'],
  ['AR', 'Argentine'], ['AM', 'Arménienne'], ['AU', 'Australienne'],
  ['AT', 'Autrichienne'], ['AZ', 'Azerbaïdjanaise'], ['BS', 'Bahamienne'],
  ['BH', 'Bahreïnienne'], ['BD', 'Bangladaise'], ['BB', 'Barbadienne'],
  ['BE', 'Belge'], ['BZ', 'Bélizienne'], ['BT', 'Bhoutanaise'],
  ['BY', 'Biélorusse'], ['BO', 'Bolivienne'], ['BA', 'Bosnienne'],
  ['BW', 'Botswanaise'], ['BR', 'Brésilienne'], ['BN', 'Brunéienne'],
  ['BG', 'Bulgare'], ['BI', 'Burundaise'], ['KH', 'Cambodgienne'],
  ['CM', 'Camerounaise'], ['CA', 'Canadienne'], ['CL', 'Chilienne'],
  ['CN', 'Chinoise'], ['CY', 'Chypriote'], ['CO', 'Colombienne'],
  ['KM', 'Comorienne'], ['CG', 'Congolaise (Brazzaville)'],
  ['CD', 'Congolaise (République démocratique)'], ['KP', 'Nord-Coréenne'],
  ['KR', 'Sud-Coréenne'], ['CR', 'Costaricaine'], ['HR', 'Croate'],
  ['CU', 'Cubaine'], ['DK', 'Danoise'], ['DJ', 'Djiboutienne'],
  ['DM', 'Dominiquaise'], ['DO', 'Dominicaine'], ['EG', 'Égyptienne'],
  ['AE', 'Émirienne'], ['EC', 'Équatorienne'], ['ER', 'Érythréenne'],
  ['ES', 'Espagnole'], ['EE', 'Estonienne'], ['SZ', 'Eswatinienne'],
  ['US', 'Américaine'], ['ET', 'Éthiopienne'], ['FJ', 'Fidjienne'],
  ['FI', 'Finlandaise'], ['FR', 'Française'], ['GA', 'Gabonaise'],
  ['GE', 'Géorgienne'], ['GR', 'Grecque'], ['GD', 'Grenadienne'],
  ['GT', 'Guatémaltèque'], ['GQ', 'Équato-Guinéenne'], ['GY', 'Guyanienne'],
  ['HT', 'Haïtienne'], ['HN', 'Hondurienne'], ['HU', 'Hongroise'],
  ['IN', 'Indienne'], ['ID', 'Indonésienne'], ['IQ', 'Irakienne'],
  ['IR', 'Iranienne'], ['IE', 'Irlandaise'], ['IS', 'Islandaise'],
  ['IL', 'Israélienne'], ['IT', 'Italienne'], ['JM', 'Jamaïcaine'],
  ['JP', 'Japonaise'], ['JO', 'Jordanienne'], ['KZ', 'Kazakhstanaise'],
  ['KE', 'Kényane'], ['KG', 'Kirghize'], ['KI', 'Kiribatienne'],
  ['XK', 'Kosovare'], ['KW', 'Koweïtienne'], ['LA', 'Laotienne'],
  ['LS', 'Lesothane'], ['LV', 'Lettone'], ['LB', 'Libanaise'],
  ['LY', 'Libyenne'], ['LI', 'Liechtensteinoise'], ['LT', 'Lituanienne'],
  ['LU', 'Luxembourgeoise'], ['MK', 'Macédonienne'], ['MG', 'Malgache'],
  ['MY', 'Malaisienne'], ['MW', 'Malawienne'], ['MV', 'Maldivienne'],
  ['MT', 'Maltaise'], ['MA', 'Marocaine'], ['MH', 'Marshallaise'],
  ['MU', 'Mauricienne'], ['MR', 'Mauritanienne'], ['MX', 'Mexicaine'],
  ['FM', 'Micronésienne'], ['MD', 'Moldave'], ['MC', 'Monégasque'],
  ['MN', 'Mongole'], ['ME', 'Monténégrine'], ['MZ', 'Mozambicaine'],
  ['MM', 'Birmane'], ['NA', 'Namibienne'], ['NR', 'Nauruane'],
  ['NP', 'Népalaise'], ['NI', 'Nicaraguayenne'], ['NO', 'Norvégienne'],
  ['NZ', 'Néo-Zélandaise'], ['NL', 'Néerlandaise'], ['OM', 'Omanaise'],
  ['UG', 'Ougandaise'], ['UZ', 'Ouzbèke'], ['PK', 'Pakistanaise'],
  ['PW', 'Paluane'], ['PS', 'Palestinienne'], ['PA', 'Panaméenne'],
  ['PG', 'Papouane-Néo-Guinéenne'], ['PY', 'Paraguayenne'], ['PE', 'Péruvienne'],
  ['PH', 'Philippine'], ['PL', 'Polonaise'], ['PT', 'Portugaise'],
  ['QA', 'Qatarienne'], ['CF', 'Centrafricaine'], ['RO', 'Roumaine'],
  ['GB', 'Britannique'], ['RU', 'Russe'], ['RW', 'Rwandaise'],
  ['KN', 'Kittitienne-et-Névicienne'], ['SM', 'Saint-Marinaise'],
  ['VC', 'Saint-Vincentaise-et-Grenadine'], ['LC', 'Saint-Lucienne'],
  ['SB', 'Salomonaise'], ['SV', 'Salvadorienne'], ['WS', 'Samoane'],
  ['ST', 'Santoméenne'], ['RS', 'Serbe'], ['SC', 'Seychelloise'],
  ['SG', 'Singapourienne'], ['SK', 'Slovaque'], ['SI', 'Slovène'],
  ['SO', 'Somalienne'], ['SD', 'Soudanaise'], ['SS', 'Sud-Soudanaise'],
  ['LK', 'Sri-Lankaise'], ['SE', 'Suédoise'], ['CH', 'Suisse'],
  ['SR', 'Surinamaise'], ['SY', 'Syrienne'], ['TJ', 'Tadjike'],
  ['TZ', 'Tanzanienne'], ['TD', 'Tchadienne'], ['CZ', 'Tchèque'],
  ['TH', 'Thaïlandaise'], ['TL', 'Est-Timoraise'], ['TO', 'Tonguienne'],
  ['TT', 'Trinidadienne'], ['TN', 'Tunisienne'], ['TM', 'Turkmène'],
  ['TR', 'Turque'], ['TV', 'Tuvaluane'], ['UA', 'Ukrainienne'],
  ['UY', 'Uruguayenne'], ['VU', 'Vanuatuane'], ['VA', 'Vaticane'],
  ['VE', 'Vénézuélienne'], ['VN', 'Vietnamienne'], ['YE', 'Yéménite'],
  ['ZM', 'Zambienne'], ['ZW', 'Zimbabwéenne'],

  // ── Ce qui n'est pas un État ──────────────────────────────────────────
  // ⚠️ L'APATRIDIE EST UN STATUT RECONNU, pas une case vide. L'omettre
  //    obligerait l'agent à inventer une nationalité — sur un acte foncier.
  ['XX', 'Apatride'],
];

/** L'ordre d'affichage : la liste elle-même, Guinéenne d'abord. */
export const LIBELLES_NATIONALITE: readonly string[] = NATIONALITES.map(([, l]) => l);

/** ⚠️ Défaut du registre guinéen — celui que la plupart des fiches porteront. */
export const NATIONALITE_PAR_DEFAUT = 'Guinéenne';

const PAR_LIBELLE = new Map(NATIONALITES.map(([c, l]) => [l, c]));
export const codeNationalite = (libelle: string): string | null =>
  PAR_LIBELLE.get(libelle) ?? null;
export const estNationaliteConnue = (v: unknown): boolean =>
  typeof v === 'string' && PAR_LIBELLE.has(v);
